/**
 * The preset picker inside `NoStructure`'s empty state (Figma `329:12603`;
 * `owner-decisions-align-329-12603-01`): choosing a ready-made hierarchy and confirming
 * writes every level in one call through the same batch route `StructureDialog`'s
 * "Salvar estrutura" uses (`useSaveStructure`, `PUT .../levels`). A level-per-request loop
 * would leave a half-built structure reachable on a network failure with nothing here to
 * recover it, so the whole preset is always one request.
 *
 * "Montar do zero" is not a third preset: it hands off to the same manual editor
 * `onCreateFirstLevel` already opened before this picker existed, so it is left disabled
 * while a preset save is in flight rather than folded into the radio group.
 */
import { ChevronRight, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ErrorState } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { Field, FieldLabel } from '../../../ui/field';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import { plural, t, type CampaignMessageKey } from '../i18n';
import { useSaveStructure } from './hooks';
import type { LevelBatchIn } from './schemas';

interface Preset {
  id: string;
  levelKeys: readonly CampaignMessageKey[];
}

const PRESETS: readonly Preset[] = [
  {
    id: 'gerente-regional-comunidade',
    levelKeys: [
      'teams.presets.level.manager',
      'teams.presets.level.regional',
      'teams.presets.level.community',
    ],
  },
  {
    id: 'coordenacao-regional-bairro-comunidade',
    levelKeys: [
      'teams.presets.level.coordination',
      'teams.presets.level.regional',
      'teams.presets.level.neighborhood',
      'teams.presets.level.community',
    ],
  },
];

function levelNames(preset: Preset): string[] {
  return preset.levelKeys.map((key) => t(key));
}

function batchOf(preset: Preset): LevelBatchIn {
  return { levels: levelNames(preset).map((name) => ({ id: null, name })) };
}

function PresetRow(props: { preset: Preset; selected: boolean }): ReactNode {
  const { preset, selected } = props;
  const id = `structure-preset-${preset.id}`;
  const names = levelNames(preset);
  return (
    <FieldLabel htmlFor={id} className="w-full">
      <Field orientation="horizontal">
        <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-1.5">
            {names.map((name, index) => (
              <span key={name} className="flex items-center gap-1.5">
                {index > 0 && <ChevronRight aria-hidden className="size-3 text-muted-foreground" />}
                <Chip tone={selected ? 'outline' : 'neutral'} size={selected ? 'md' : 'sm'}>
                  {name}
                </Chip>
              </span>
            ))}
          </span>
          <span className="shrink-0 text-caption text-muted-foreground">
            {plural('teams.presets.levelCount.other', names.length, { count: names.length })}
          </span>
        </div>
        <RadioGroupItem value={preset.id} id={id} />
      </Field>
    </FieldLabel>
  );
}

export function StructurePresets(props: {
  campaignId: string;
  onBuildFromScratch: () => void;
}): ReactNode {
  const { campaignId, onBuildFromScratch } = props;
  const [selected, setSelected] = useState<string>(PRESETS[0]!.id);
  const saveMutation = useSaveStructure(campaignId);
  const pending = saveMutation.isPending;

  function handleUse(): void {
    const preset = PRESETS.find((candidate) => candidate.id === selected);
    if (preset === undefined) return;
    saveMutation.mutate(batchOf(preset));
  }

  return (
    <div className="flex w-full flex-col items-stretch gap-3">
      <RadioGroup
        aria-label={t('teams.presets.groupLabel')}
        value={selected}
        onValueChange={setSelected}
        disabled={pending}
        className="gap-2"
      >
        {PRESETS.map((preset) => (
          <PresetRow key={preset.id} preset={preset} selected={preset.id === selected} />
        ))}
      </RadioGroup>

      <Button
        type="button"
        variant="outline"
        onClick={onBuildFromScratch}
        disabled={pending}
        className="h-auto w-full justify-start gap-2 whitespace-normal rounded-lg border-dashed border-border px-4 py-3 text-left"
      >
        <Plus aria-hidden className="size-3.75 shrink-0" />
        <span className="flex-1 text-label text-foreground">
          {t('teams.presets.scratch.title')}
        </span>
        <span className="text-caption text-muted-foreground">
          {t('teams.presets.scratch.description')}
        </span>
      </Button>

      {saveMutation.isError && (
        <ErrorState
          failure={{ kind: 'invalid', field: null, message: t('teams.editor.save.failed') }}
        />
      )}

      <Button type="button" onClick={handleUse} disabled={pending}>
        {pending ? t('teams.editor.saving') : t('teams.presets.action')}
      </Button>
    </div>
  );
}
